
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import UserModule from '@/store/modules/User';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AxiosError } from 'axios';
import api from '@/service/api';

const userModule = getModule(UserModule, store);

@Component
export default class Login extends Vue {
    @Prop({ default: true }) public showFacebookAuth!: boolean;
    public lostPass = false;
    public recoverPassword = false;

    public loginEmail = '';
    public loginPassword = '';
    public login2faCode = '';
    public twofaDigits = ['', '', '', '', '', ''];

    public lostPasswordEmail = '';
    private readonly lostPasswordEmailPattern = /^[\w.-]+@([\w-]+\.)+[\w-]+$/;

    invalidLogin = false;
    loginErrorMessage = 'Błędny email lub hasło';
    
    // Loading states
    public isLoadingFb = false;
    public isLoadingPassword = false;
    public isLoading2fa = false;

    get twoFactorModal() {
        return userModule.twoFactorRequired;
    }

    set twoFactorModal(value: boolean) {
        if (!value) {
            userModule.resolve2FA();
            userModule.setTwoFactorErrors([]);
            this.login2faCode = '';
            this.twofaDigits = ['', '', '', '', '', ''];
        }
    }

    get twoFactorErrors() {
        return userModule.twoFactorErrors;
    }

    public async fbLogin() {
        try {
            this.isLoadingFb = true;
            await userModule.authenticateFacebook({ redirectOnSuccess: false });
            this.$emit('auth-success');
        } catch (e) {
            if (e.response && e.response.status === 403) {
                userModule.setRegistrationResolver(async (code: string) => {
                    await userModule.authenticateFacebook(code);
                });
            } else {
                throw e;
            }
        } finally {
            this.isLoadingFb = false;
        }
    }

    get twoFactorMethod() {
        return userModule.twoFactorTicket?.user?.twoFactorMethod;
    }

    public async passwordLogin() {
        if (this.isLoadingFb || this.isLoadingPassword) {
            return;
        }
        try {
            this.isLoadingPassword = true;
            this.invalidLogin = false; // Reset any previous error
            this.loginErrorMessage = 'Błędny email lub hasło';
            await userModule.authenticatePassword({
                email: this.loginEmail,
                password: this.loginPassword,
                redirectOnSuccess: false,
            });
            if (!userModule.twoFactorRequired) {
                this.$emit('auth-success');
            }
        } catch (e) {
            if (e instanceof AxiosError) {
                const responseStatus = e.response?.status;
                const errorCode = (e.response?.data as { errorCode?: string } | undefined)?.errorCode;
                if (errorCode === 'email_not_confirmed' || responseStatus === 403) {
                    this.loginErrorMessage = 'Potwierdź konto klikając link w mailu';
                } else {
                    this.loginErrorMessage = 'Błędny email lub hasło';
                }
                this.invalidLogin = true;
            } else {
                throw e;
            }
        } finally {
            this.isLoadingPassword = false;
        }
    }

    public async requestPasswordRecovery() {
        await api.requestPasswordRecovery(this.lostPasswordEmail);
        this.lostPass = false;
        window.setTimeout(() => {
            this.recoverPassword = true;
        }, 220);
    }

    get canSubmitPasswordRecovery() {
        return this.lostPasswordEmailPattern.test(this.lostPasswordEmail.trim());
    }

    public async submit2fa() {
        if (this.isLoading2fa) {
            return;
        }

        const code = this.twofaDigits.join('').trim();
        if (!code) {
            userModule.setTwoFactorErrors(['Kod 2FA jest wymagany']);
            return;
        }
        if (!/^\d{6}$/.test(code)) {
            userModule.setTwoFactorErrors(['Kod 2FA musi mieć 6 cyfr']);
            return;
        }

        this.login2faCode = code;
        this.isLoading2fa = true;
        try {
            const authenticated = await userModule.authenticate2FA({ code, redirectOnSuccess: false });
            if (authenticated) {
                this.$emit('auth-success');
            }
        } catch (e) {
            if ((e as any)?.response) {
                userModule.setTwoFactorErrors(['Nie udało się zweryfikować kodu 2FA. Spróbuj ponownie.']);
                return;
            }
            throw e;
        } finally {
            this.isLoading2fa = false;
        }
    }

    public onTwofaDigitInput(index: number, event: Event) {
        const target = event.target as HTMLInputElement;
        const nextDigit = (target.value || '').replace(/\D/g, '').slice(-1);
        this.$set(this.twofaDigits, index, nextDigit);

        this.login2faCode = this.twofaDigits.join('');
        if (userModule.twoFactorErrors.length) {
            userModule.setTwoFactorErrors([]);
        }

        if (this.login2faCode.length === 6 && /^\d{6}$/.test(this.login2faCode)) {
            this.submit2fa();
            return;
        }

        if (nextDigit && index < this.twofaDigits.length - 1) {
            this.focusTwofaDigit(index + 1);
        }
    }

    public onTwofaDigitKeydown(index: number, event: KeyboardEvent) {
        const target = event.target as HTMLInputElement;

        if (event.key === 'Backspace' && !target.value && index > 0) {
            this.focusTwofaDigit(index - 1);
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            this.focusTwofaDigit(index - 1);
            return;
        }

        if (event.key === 'ArrowRight' && index < this.twofaDigits.length - 1) {
            event.preventDefault();
            this.focusTwofaDigit(index + 1);
        }
    }

    public onTwofaPaste(event: ClipboardEvent) {
        const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
        if (!pasted) {
            return;
        }

        this.twofaDigits = ['', '', '', '', '', ''];
        pasted.split('').forEach((digit, index) => {
            this.$set(this.twofaDigits, index, digit);
        });

        this.login2faCode = this.twofaDigits.join('');
        if (userModule.twoFactorErrors.length) {
            userModule.setTwoFactorErrors([]);
        }

        if (this.login2faCode.length === 6 && /^\d{6}$/.test(this.login2faCode)) {
            this.submit2fa();
            return;
        }

        const focusIndex = Math.min(pasted.length, this.twofaDigits.length - 1);
        this.focusTwofaDigit(focusIndex);
    }

    private focusTwofaDigit(index: number) {
        this.$nextTick(() => {
            const inputRef = this.$refs[`twofaDigit${index}`] as HTMLInputElement[] | HTMLInputElement | undefined;
            const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
            input?.focus?.();
            input?.select?.();
        });
    }

    @Watch('twoFactorModal')
    onTwoFactorModalChange(isOpen: boolean) {
        if (!isOpen) {
            return;
        }

        userModule.setTwoFactorErrors([]);
        this.login2faCode = '';
        this.twofaDigits = ['', '', '', '', '', ''];

        this.$nextTick(() => {
            setTimeout(() => {
                this.focusTwofaDigit(0);
            }, 120);
        });
    }
}

